import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportsService, ApprovalSlaRow, ApprovalSlaReportFilters } from '../../services/api/reports';
import { departmentsService } from '../../services/api/departments';
import { Department } from '../../types';
import './ApprovalSlaReport.css';

// Helper function to get current month date range
const getCurrentMonthRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const startDate = new Date(year, month, 1);
  const endDate = new Date(year, month + 1, 0); // Last day of current month
  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  };
};

const ApprovalSlaReport: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ApprovalSlaRow[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  
  // Get current month start and end dates
  const monthRange = useMemo(() => getCurrentMonthRange(), []);
  
  const [filters, setFilters] = useState<ApprovalSlaReportFilters>({
    startDate: monthRange.startDate,
    endDate: monthRange.endDate,
    includeSubtree: false,
  });

  useEffect(() => {
    loadDepartments();
    loadReport();
  }, []);

  useEffect(() => {
    loadReport();
  }, [filters]);

  const loadDepartments = async () => {
    try {
      const depts = await departmentsService.getAll();
      setDepartments(depts);
    } catch (error) {
      console.error('Failed to load departments:', error);
    }
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const result = await reportsService.getApprovalSlaReport(filters);
      setData(result);
    } catch (error) {
      console.error('Failed to load approval SLA report:', error);
      alert('Failed to load report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof ApprovalSlaReportFilters, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      await reportsService.exportApprovalSlaReport(filters);
    } catch (error) {
      console.error('Failed to export report:', error);
      alert('Failed to export report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const totalPending = data.reduce((sum, row) => sum + row.pendingCount, 0);
  const totalOverdue = data.reduce((sum, row) => sum + row.overdueCount, 0);
  const avgDecisionTime = data.length > 0
    ? data.reduce((sum, row) => sum + row.avgDecisionHours, 0) / data.length
    : 0;

  return (
    <div className="approval-sla-report">
      <div className="report-header">
        <button className="back-button" onClick={() => navigate('/reports')}>
          ← Back to Reports
        </button>
        <div>
          <h1>Approval SLA Report (RPT-APP-01)</h1>
          <p>View pending approvals, overdue requests, and average decision times</p>
        </div>
      </div>

      <div className="report-filters">
        <div className="filter-group">
          <label>Start Date</label>
          <input
            type="date"
            value={filters.startDate || ''}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>End Date</label>
          <input
            type="date"
            value={filters.endDate || ''}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>Department</label>
          <select
            value={filters.departmentId || ''}
            onChange={(e) =>
              handleFilterChange('departmentId', e.target.value ? Number(e.target.value) : undefined)
            }
          >
            <option value="">All Departments</option>
            {departments.map((dept) => (
              <option key={dept.id} value={dept.id}>
                {dept.name}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>
            <input
              type="checkbox"
              checked={filters.includeSubtree || false}
              onChange={(e) => handleFilterChange('includeSubtree', e.target.checked)}
            />
            Include Sub-departments
          </label>
        </div>
        <button className="export-button" onClick={handleExport} disabled={loading}>
          {loading ? 'Exporting...' : 'Export Excel'}
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading report...</div>
      ) : (
        <>
          <div className="summary-cards">
            <div className="summary-card">
              <div className="summary-label">Total Pending</div>
              <div className="summary-value">{totalPending}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Total Overdue</div>
              <div className="summary-value overdue">{totalOverdue}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Avg Decision Time</div>
              <div className="summary-value">{avgDecisionTime.toFixed(1)}h</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Total Managers</div>
              <div className="summary-value">{data.length}</div>
            </div>
          </div>

          <div className="report-table-container">
            <table className="report-table">
              <thead>
                <tr>
                  <th>Manager Name</th>
                  <th>Email</th>
                  <th>Department</th>
                  <th>Pending</th>
                  <th>Overdue</th>
                  <th>Avg Decision (hours)</th>
                  <th>Total Decisions</th>
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="no-data">
                      No data available
                    </td>
                  </tr>
                ) : (
                  data.map((row) => (
                    <tr key={row.managerId}>
                      <td>{row.managerName}</td>
                      <td>{row.managerEmail}</td>
                      <td>{row.departmentName}</td>
                      <td className="number-cell">{row.pendingCount}</td>
                      <td className={`number-cell ${row.overdueCount > 0 ? 'overdue' : ''}`}>
                        {row.overdueCount}
                      </td>
                      <td className="number-cell">{row.avgDecisionHours.toFixed(1)}</td>
                      <td className="number-cell">{row.totalDecisions}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default ApprovalSlaReport;
